import type { TranslationLanguage } from '../ts/types';
import { languagesList } from './language-list';

/**
 * TELV1-538 — Normalize a BCP-47 locale tag (or a bare language code) into
 * one of Tellet's 65 canonical translation keys.
 *
 * ## Why this exists
 *
 * The chat app used to derive the respondent's language with
 * `new Intl.Locale(navigator.language).language`, which returns ONLY the
 * primary subtag. For a Taiwanese respondent that turns `zh-TW` into `zh` —
 * throwing away exactly the subtag that distinguishes Traditional from
 * Simplified script — so the consent screen rendered Simplified Chinese on a
 * Traditional Chinese study. Taiwanese respondents read Simplified as the
 * PRC's script, so this destroyed trust on the very first screen.
 *
 * Primary-subtag stripping is also wrong for a second, quieter class: several
 * locales browsers really emit (`he-IL`, `nb-NO`, `nn-NO`, `fil-PH`,
 * `in-ID`) strip to a code that is NOT a key in any of our translation
 * tables, so those respondents silently fell all the way back to English.
 * `he-IL` was the worst of them: `he` IS in `RTL_LANGUAGE_CODES`, so the
 * document flipped to RTL and then rendered English copy inside it.
 *
 * ## Resolution
 *
 * Lowercase, then walk the tag from most to least specific, stopping at the
 * first alias or canonical code that matches:
 *
 *   `zh-Hant-TW` → `zh-hant-tw` (miss) → `zh-hant` (alias) → `zh-TW`
 *   `pt-BR`      → `pt-br`      (miss) → `pt`      (canonical)
 *   `he-IL`      → `he-il`      (miss) → `he`      (alias)    → `iw`
 *
 * Returns `null` when nothing matches, so callers can distinguish "not a
 * locale" (e.g. the full name `"Spanish"`) from a real resolution and fall
 * through to their own lookup.
 */

/**
 * Locale forms that are NOT themselves canonical Tellet codes. Keyed
 * lowercase. Anything already present in `languagesList` (`zh-TW`, `yue`,
 * `pt`, …) resolves without an entry here.
 */
const LOCALE_ALIASES: Record<string, TranslationLanguage> = {
  // --- Chinese script variants -------------------------------------------
  // The script subtag, not the region, decides which copy is correct.
  'zh-hant': 'zh-TW',
  'zh-hans': 'zh',
  // Hong Kong and Macau write Standard Written Chinese in TRADITIONAL script.
  //
  // NOTE: this DELIBERATELY diverges from the backend's `canonicalizeLanguage`,
  // which maps `zh-HK` → `Cantonese`. That table answers "what will this
  // respondent SPEAK" (it feeds Whisper and the interview language) and
  // Cantonese is the right answer there. This table answers "which copy do we
  // RENDER", and the `yue` block is colloquial *written* Cantonese
  // (`呢個唔係問卷` / `開始啦`) — the wrong register for a legal consent
  // notice. An HK respondent who genuinely wants Cantonese still gets it via
  // the picker or an explicit `?language=yue`.
  'zh-hk': 'zh-TW',
  'zh-mo': 'zh-TW',
  // No entry for `zh-CN` / `zh-SG` on purpose: mainland China and Singapore
  // both write Simplified, and both already reach it by degrading to the
  // canonical `zh`. Only the regions that would land on the WRONG script need
  // to be listed here.

  // --- Legacy / divergent primary subtags --------------------------------
  // Each of these is a real `navigator.language` value whose primary subtag
  // is absent from our translation tables, so stripping alone yields English.
  he: 'iw', // modern ISO 639-1; Tellet canonicalizes on the legacy `iw`
  nb: 'no', // Bokmål  → Tellet's umbrella `Norwegian`
  nn: 'no', // Nynorsk → same
  in: 'id', // deprecated ISO code, still emitted by older Android browsers
  fil: 'tl', // Filipino → Tellet's `Tagalog`
};

/** Lowercased canonical code → the canonical code with its real casing. */
const CANONICAL_BY_LOWER = new Map<string, TranslationLanguage>(
  languagesList.language.map((lang) => [lang.code.toLowerCase(), lang.code as TranslationLanguage]),
);

export function normalizeLanguageCode(input?: string | null): TranslationLanguage | null {
  if (typeof input !== 'string') return null;
  // Browsers emit `-`; some panel providers and Android emit `_`.
  const tag = input.trim().toLowerCase().replace(/_/g, '-');
  if (!tag) return null;

  const subtags = tag.split('-');
  // Most specific first, so `zh-hant-tw` matches the `zh-hant` alias before
  // it can degrade to the `zh` (Simplified) canonical code.
  for (let { length } = subtags; length > 0; length -= 1) {
    const candidate = subtags.slice(0, length).join('-');
    const alias = LOCALE_ALIASES[candidate];
    if (alias) return alias;
    const canonical = CANONICAL_BY_LOWER.get(candidate);
    if (canonical) return canonical;
  }

  return null;
}

/**
 * Type guard for "this string is one of the 65 canonical translation keys".
 * Lets callers clamp an arbitrary code to `TranslationLanguage` instead of
 * casting, so an unknown value can never reach a translation table lookup.
 */
export function isTranslationLanguage(input?: string | null): input is TranslationLanguage {
  if (typeof input !== 'string') return false;
  return CANONICAL_BY_LOWER.get(input.toLowerCase()) === input;
}
